export const SAAS_THEME_STORAGE_KEY = "saas-template-theme";

/** Giá trị được ÁP vào `<html data-theme=...>`. Mỗi giá trị PHẢI có một khối CSS trong tokens.css. */
export const SAAS_THEMES = ["light", "dark", "studio", "retro"] as const;

export type SaasTheme = (typeof SAAS_THEMES)[number];

/**
 * Giá trị NGƯỜI DÙNG chọn và được lưu xuống localStorage.
 *
 * `"system"` KHÔNG nằm trong `SAAS_THEMES` — nếu nằm, `applySaasThemeToDocument` sẽ sinh
 * `data-theme="system"` mà không khối CSS nào khớp và trang mất sạch màu. Nó là *preference*,
 * phải qua `resolveSaasTheme()` mới thành theme thật.
 */
export const SAAS_THEME_PREFERENCES = [...SAAS_THEMES, "system"] as const;

export type SaasThemePreference = (typeof SAAS_THEME_PREFERENCES)[number];

export function isSaasTheme(value: string | null | undefined): value is SaasTheme {
  return value !== null && value !== undefined && (SAAS_THEMES as readonly string[]).includes(value);
}

export function isSaasThemePreference(value: string | null | undefined): value is SaasThemePreference {
  return (
    value !== null &&
    value !== undefined &&
    (SAAS_THEME_PREFERENCES as readonly string[]).includes(value)
  );
}

/** Legacy `forest` / invalid values → `fallback` (default light). */
export function normalizeStoredTheme(
  stored: string | null,
  fallback: SaasThemePreference = "light",
): SaasThemePreference {
  if (stored === "forest") return "retro";
  if (isSaasThemePreference(stored)) return stored;
  return fallback;
}

/** `"system"` → theo `prefers-color-scheme`; ngoài trình duyệt thì mặc định `light`. */
export function resolveSaasTheme(preference: SaasThemePreference): SaasTheme {
  if (preference !== "system") return preference;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Theo dõi `prefers-color-scheme` của OS. Trả hàm huỷ đăng ký.
 *
 * Tách khỏi provider để test được: `matchMedia` giả trong test dựng được listener và gọi tay,
 * còn chế độ giả lập media của DevTools chỉ đổi `matches` mà KHÔNG bắn event `change` — tức không
 * thể kiểm đường này bằng trình duyệt.
 */
export function subscribeSystemTheme(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

export function applySaasThemeToDocument(root: HTMLElement, theme: SaasTheme): void {
  root.setAttribute("data-theme", theme);
  root.classList.toggle("dark", theme === "dark");
  syncThemeColorMeta(root);
}

/**
 * `<meta name="theme-color">` PHẢI đi theo nền của theme đang dùng.
 *
 * Khi app chạy dạng đã cài (PWA standalone), hệ điều hành tô thanh trạng thái trên và thanh điều
 * hướng dưới bằng màu này. Không khai thẻ đó thì Android rơi về `theme_color` trong manifest — một
 * giá trị CỐ ĐỊNH chọn từ lúc build — nên đổi theme là hai dải kia đứng im, và người dùng thấy một
 * viền lạ kẹp lấy app. Đó cũng là lý do màn khởi động (dùng `background_color`) trông khớp, còn app
 * chạy rồi thì không.
 *
 * Đọc thẳng `--background` đang tính được thay vì chép bảng màu: bảng màu nằm ở `tokens.css`, chép
 * sang đây là tạo bản thứ hai, lệch ngay lần đổi token đầu tiên.
 */
function syncThemeColorMeta(root: HTMLElement): void {
  if (typeof document === "undefined") return;
  const hex = rgbTripletToHex(getComputedStyle(root).getPropertyValue("--background"));
  /* CSS chưa nạp ⇒ chưa có gì để khai; khai thiếu còn hơn khai sai màu. */
  if (!hex) return;

  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = hex;
}

/**
 * `"247 243 236"` → `"#f7f3ec"`.
 *
 * Hex chứ không phải `rgb(247 243 236)`: cú pháp cách-trắng là CSS Color 4, và bộ phân tích
 * `theme-color` của WebView cũ bỏ qua giá trị nó không hiểu — bỏ qua nghĩa là quay về màu manifest,
 * tức đúng cái lỗi đang chữa, mà lại không thấy gì sai trong code.
 */
function rgbTripletToHex(triplet: string): string | null {
  const parts = triplet.trim().split(/[\s,]+/).filter(Boolean).map(Number);
  if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const hex = parts
    .slice(0, 3)
    .map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0"))
    .join("");
  return `#${hex}`;
}

/**
 * Script inline chạy TRƯỚC khi React hydrate, để tránh nháy trắng (FOUC) khi tải lại trang ở
 * chế độ tối. Nhúng bằng `dangerouslySetInnerHTML` trong `<head>` (Next) hoặc chèn vào `<head>`
 * trước `render()` (Vite).
 *
 * `legacyStorageKey` phục vụ app đã đổi khoá lưu trữ: đọc khoá mới trước, không thấy thì đọc khoá cũ.
 */
export function saasThemeInitScript(options?: {
  storageKey?: string;
  defaultPreference?: SaasThemePreference;
  legacyStorageKey?: string;
}): string {
  const key = options?.storageKey ?? SAAS_THEME_STORAGE_KEY;
  const fallback = options?.defaultPreference ?? "light";
  const legacy = options?.legacyStorageKey ?? null;

  return (
    `(function(){try{` +
    `var t=localStorage.getItem(${JSON.stringify(key)});` +
    (legacy ? `if(!t)t=localStorage.getItem(${JSON.stringify(legacy)});` : "") +
    `if(t==="forest")t="retro";` +
    `if(!t||${JSON.stringify([...SAAS_THEME_PREFERENCES])}.indexOf(t)<0)t=${JSON.stringify(fallback)};` +
    `if(t==="system")t=window.matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light";` +
    `var r=document.documentElement;` +
    `r.setAttribute("data-theme",t);r.classList.toggle("dark",t==="dark");` +
    `}catch(e){}})();`
  );
}
